import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { apiGet } from '@/lib/api/client.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { useAuthStore } from '@/stores/auth.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

type Report = {
  byPartSupplier: Array<{
    partName: string;
    supplierName: string;
    failures: number;
    installed: number;
    failureRatePct: number | null;
    avgDaysToFailure: number | null;
    totalCost: string;
    credited: string;
    denied: string;
    netLoss: string;
  }>;
  byRepairType: Array<{ serviceName: string; repairs: number; rework: number; reworkRatePct: number }>;
  overall: {
    warrantyTickets: number;
    allTickets: number;
    warrantyTicketPct: number;
    reworkLaborHours: number;
    reworkLaborCost: string;
    reworkPartsCost: string;
    supplierCredits: string;
    netCost: string;
  };
  internalDamage: {
    byFailureType: Array<{ failureType: string; count: number; cost: string }>;
    byTechnician: Array<{ name: string; count: number; cost: string }>;
    writtenOff: string;
  };
  byTechnician: Array<{
    name: string;
    repairs: number;
    rework: number;
    reworkRatePct: number;
    jobMix: string[];
  }> | null;
  technicianNote: string;
  openRmaValue: {
    summary: string;
    quarantineBySupplier: Array<{ supplierName: string; total: string }>;
    awaitingBySupplier: Array<{ supplierName: string; total: string; expectedCredit: string }>;
  };
};

export function RmaAndReworkReportPage() {
  const f = useReportFilters();
  const role = useAuthStore((s) => s.user?.role);
  const q = useQuery({
    queryKey: ['rma-rework-report', f.period, f.storeId, f.dateFrom, f.dateTo],
    queryFn: () =>
      apiGet<Report>('/api/v1/reports/rma-and-rework', {
        params: {
          period: f.period,
          storeId: f.storeId || undefined,
          dateFrom: f.dateFrom || undefined,
          dateTo: f.dateTo || undefined,
        },
      }).then((r) => r.data),
  });

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading report…</div>;
  if (q.isError || !q.data) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load this report.</div>;
  const data = q.data;
  const showTechs = role === 'tenant_admin' || role === 'platform_admin';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-950">RMA and rework</h1>
        <p className="text-sm text-slate-500">Where parts fail, what rework costs, and what suppliers still owe.</p>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-950">
        <p className="text-lg font-semibold">{data.openRmaValue.summary}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Warranty tickets" value={`${data.overall.warrantyTickets} · ${data.overall.warrantyTicketPct}%`} />
        <Stat label="Rework labor" value={`${formatCurrency(data.overall.reworkLaborCost)} · ${data.overall.reworkLaborHours}h`} />
        <Stat label="Rework parts" value={formatCurrency(data.overall.reworkPartsCost)} />
        <Stat label="Net after credits" value={formatCurrency(data.overall.netCost)} />
      </div>

      <Section title="By part and supplier">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Part</th>
              <th className="px-4 py-3">Supplier</th>
              <th className="px-4 py-3">Failures</th>
              <th className="px-4 py-3">Avg days</th>
              <th className="px-4 py-3">Cost</th>
              <th className="px-4 py-3">Credited</th>
              <th className="px-4 py-3">Denied</th>
              <th className="px-4 py-3">Net loss</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.byPartSupplier.map((row) => (
              <tr key={`${row.partName}-${row.supplierName}`}>
                <td className="px-4 py-3 font-medium">{row.partName}</td>
                <td className="px-4 py-3">{row.supplierName}</td>
                <td className="px-4 py-3">
                  {row.failures}
                  {row.failureRatePct != null ? ` · ${row.failureRatePct}% of ${row.installed}` : ''}
                </td>
                <td className="px-4 py-3">{row.avgDaysToFailure ?? '—'}</td>
                <td className="px-4 py-3">{formatCurrency(row.totalCost)}</td>
                <td className="px-4 py-3">{formatCurrency(row.credited)}</td>
                <td className="px-4 py-3">{formatCurrency(row.denied)}</td>
                <td className="px-4 py-3">{formatCurrency(row.netLoss)}</td>
              </tr>
            ))}
            {data.byPartSupplier.length === 0 ? <Empty cols={8} /> : null}
          </tbody>
        </table>
      </Section>

      <Section title="By repair type">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Repair</th>
              <th className="px-4 py-3">Jobs</th>
              <th className="px-4 py-3">Rework</th>
              <th className="px-4 py-3">Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.byRepairType.map((row) => (
              <tr key={row.serviceName}>
                <td className="px-4 py-3 font-medium">{row.serviceName}</td>
                <td className="px-4 py-3">{row.repairs}</td>
                <td className="px-4 py-3">{row.rework}</td>
                <td className="px-4 py-3">{row.reworkRatePct}%</td>
              </tr>
            ))}
            {data.byRepairType.length === 0 ? <Empty cols={4} /> : null}
          </tbody>
        </table>
      </Section>

      <Section title="Internal damage">
        <p className="px-4 py-2 text-sm text-slate-600">Written off {formatCurrency(data.internalDamage.writtenOff)}</p>
        <div className="grid gap-4 md:grid-cols-2">
          <ul className="space-y-1 px-4 pb-4 text-sm">
            {data.internalDamage.byFailureType.map((row) => (
              <li key={row.failureType}>{prettyLabel(row.failureType)} · {row.count} · {formatCurrency(row.cost)}</li>
            ))}
          </ul>
          <ul className="space-y-1 px-4 pb-4 text-sm">
            {data.internalDamage.byTechnician.map((row) => (
              <li key={row.name}>{row.name} · {row.count} · {formatCurrency(row.cost)}</li>
            ))}
          </ul>
        </div>
      </Section>

      <div className="grid gap-4 md:grid-cols-2">
        <Section title="In quarantine">
          <ul className="space-y-1 px-4 py-3 text-sm">
            {data.openRmaValue.quarantineBySupplier.map((row) => (
              <li key={row.supplierName}>{row.supplierName} · {formatCurrency(row.total)}</li>
            ))}
          </ul>
        </Section>
        <Section title="Awaiting supplier credit">
          <ul className="space-y-1 px-4 py-3 text-sm">
            {data.openRmaValue.awaitingBySupplier.map((row) => (
              <li key={row.supplierName}>{row.supplierName} · expected {formatCurrency(row.expectedCredit)}</li>
            ))}
          </ul>
        </Section>
      </div>

      {showTechs && data.byTechnician ? (
        <Section title="By technician">
          <p className="px-4 py-3 text-sm text-slate-600">{data.technicianNote}</p>
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Technician</th>
                <th className="px-4 py-3">Volume</th>
                <th className="px-4 py-3">Rework</th>
                <th className="px-4 py-3">Rate</th>
                <th className="px-4 py-3">Job mix</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.byTechnician.map((row) => (
                <tr key={row.name}>
                  <td className="px-4 py-3 font-medium">{row.name}</td>
                  <td className="px-4 py-3">{row.repairs}</td>
                  <td className="px-4 py-3">{row.rework}</td>
                  <td className="px-4 py-3">{row.reworkRatePct}%</td>
                  <td className="px-4 py-3">{row.jobMix.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border bg-white px-4 py-3 shadow-sm">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-bold text-slate-950">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <h2 className="border-b px-4 py-3 font-semibold text-slate-950">{title}</h2>
      {children}
    </section>
  );
}

function Empty({ cols }: { cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-6 text-center text-slate-500">Nothing in this period.</td>
    </tr>
  );
}
